/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef, useEffect } from 'react';
import { GripVertical, Pin, PinOff, RefreshCw, Check, X, Filter, Info, MoreVertical, Download, LogOut } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Dashboard } from '../types/Dashboard';
import { UpdateDashboardRequest } from '../types/DashboardRequests';
import { DashboardContent } from './DashboardContent';
import { DashboardSettings } from './DashboardSettings';
import { cn } from '@/infrastructure/lib/utils';
import { IntegrationBaseService } from '@/features/Integrations/services/integrationsBaseService';
import { AgentBaseService } from '@/features/Agents/services/agentBaseService';
import { getLocalizedText } from '@/shared/utils/i18nHelper';
import { DashboardService } from '../services/dashboardService';
import { useToast } from '@/shared/components/ui/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';

interface DashboardCardProps {
  dashboard: Dashboard;
  onUpdate: (params: { dashboardId: string; request: UpdateDashboardRequest }) => void;
  onRemove: (dashboardId: string) => void;
  // "Remover do slot" — dashboard some, slot continua vazio. Passada
  // quando o dashboard está em um slot (qualquer layout).
  onUnassignSlot?: () => void;
  // Adicional ao "Remover": também remove o slot do layout. Passada
  // somente em layouts editáveis (não vem em layouts sugeridos nem free
  // grid). Quando ausente, "Remover" só esconde a dashboard.
  onRemoveSlot?: () => void;
}

export const DashboardCard = ({ dashboard, onUpdate, onRemove, onUnassignSlot, onRemoveSlot }: DashboardCardProps) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language?.split('-')[0] || 'pt';
  const { toast } = useToast();

  // Helper para parsear campos que podem vir como JSON string
  const parseIfJSON = (value: any) => {
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  };

  // Helper para obter o nome traduzido do dashboard
  const getDashboardName = () => {
    if (dashboard.customName) {
      return getLocalizedText(parseIfJSON(dashboard.customName), currentLanguage);
    }
    return getLocalizedText(parseIfJSON(dashboard.name), currentLanguage);
  };

  // Helper para obter a descrição traduzida do dashboard
  const getDashboardDescription = () => {
    return getLocalizedText(parseIfJSON(dashboard.description), currentLanguage);
  };

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(getDashboardName());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const refetchRef = useRef<(() => void) | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Buscar integration base relacionada se source_type for 'integration'
  const { data: integrationBase } = useQuery({
    queryKey: ['integration-base', dashboard.source_id],
    queryFn: () => IntegrationBaseService.getById(dashboard.source_id),
    enabled: dashboard.source_type === 'integration' && !!dashboard.source_id,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });

  // PBI #78540 — agent-owned dashboards: usa a logo do agent_base no
  // drag-handle (mesmo padrão das integrations). source_id é o agent_base.id.
  const { data: agentBase } = useQuery({
    queryKey: ['agent-base', dashboard.source_id],
    queryFn: () => AgentBaseService.getById(dashboard.source_id),
    enabled: dashboard.source_type === 'agent' && !!dashboard.source_id,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (isEditingName && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingName]);

  const handleSaveName = () => {
    if (editedName.trim() && editedName !== (dashboard.customName || dashboard.name)) {
      onUpdate({
        dashboardId: dashboard.id,
        request: { customName: editedName.trim() },
      });
    }
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setEditedName(dashboard.customName || dashboard.name);
    setIsEditingName(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleTogglePin = () => {
    // "Remover": esconde a dashboard nas preferences e, se aplicável,
    // remove o slot do layout (apenas em layouts editáveis).
    onRemove(dashboard.id);
    onRemoveSlot?.();
  };

  const handleRefresh = async () => {
    if (refetchRef.current && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await refetchRef.current();
      } finally {
        setTimeout(() => setIsRefreshing(false), 500);
      }
    }
  };

  const handleRefreshReady = (refetch: () => void) => {
    refetchRef.current = refetch;
  };

  const handleExportCSV = async () => {
    try {
      toast({
        title: t('dashboards.exportCSV'),
        description: 'Exportando dados...',
      });

      // Buscar dados da dashboard usando o serviço correto
      const filters = dashboard.filters?.reduce((acc, filter) => {
        acc[filter.id] = filter.value;
        return acc;
      }, {} as Record<string, any>);

      // Usar o dashboard id
      const response = await DashboardService.getDashboardData(dashboard.id, filters);
      const data = response?.data?.data;
      
      // Converter dados para CSV
      if (!data || (Array.isArray(data) && data.length === 0)) {
        toast({
          title: 'Sem dados',
          description: 'Não há dados para exportar.',
          variant: 'destructive',
        });
        return;
      }

      let csvContent = '';
      
      if (Array.isArray(data)) {
        // Obter cabeçalhos (chaves do primeiro objeto)
        const headers = Object.keys(data[0]).filter(key => !key.startsWith('@'));
        csvContent = headers.join(',') + '\n';
        
        // Adicionar linhas
        data.forEach((row: any) => {
          const values = headers.map(header => {
            const value = row[header];
            // Escapar valores com vírgulas ou aspas
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value ?? '';
          });
          csvContent += values.join(',') + '\n';
        });
      } else if (typeof data === 'object') {
        // Para dados em formato objeto (como gauge-multiple)
        csvContent = 'Key,Value\n';
        Object.entries(data).forEach(([key, value]: [string, any]) => {
          const val = typeof value === 'object' ? value.value : value;
          csvContent += `${key},${val}\n`;
        });
      }

      // Criar blob e fazer download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const dashboardName = getDashboardName().replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.setAttribute('href', url);
      link.setAttribute('download', `${dashboardName}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: t('dashboards.export.success'),
        description: t('dashboards.export.successDescription'),
      });
    } catch {
      toast({
        title: t('dashboards.export.error'),
        description: t('dashboards.export.errorDescription'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="h-full w-full">
      <Card className="p-4 h-full flex flex-col">
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div
              className="dashboard-drag-handle h-6 w-6 cursor-grab active:cursor-grabbing shrink-0 flex items-center justify-center"
            >
              {integrationBase?.logo_url ? (
                <img
                  src={integrationBase.logo_url}
                  alt={integrationBase.label}
                  className="h-5 w-5 object-contain"
                  title={integrationBase.label}
                />
              ) : agentBase?.logo_url ? (
                <img
                  src={agentBase.logo_url}
                  alt={agentBase.name ?? 'agent'}
                  className="h-5 w-5 object-contain"
                  title={agentBase.name ?? undefined}
                />
              ) : (
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              )}
            </div>

            {isEditingName ? (
              <div className="flex items-center gap-1 flex-1">
                <Input
                  ref={inputRef}
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSaveName}
                  className="h-8 text-sm font-semibold"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={handleSaveName}
                >
                  <Check className="h-3 w-3 text-green-600" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={handleCancelEdit}
                >
                  <X className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <h3
                  className="font-semibold text-sm truncate cursor-pointer hover:text-primary transition-colors"
                  onClick={() => setIsEditingName(true)}
                  title={getDashboardName()}
                >
                  {getDashboardName()}
                </h3>
                <TooltipProvider>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors cursor-help shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-sm">{getDashboardDescription()}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setShowSettings(true);
                  }}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {t('dashboards.filters', 'Filtros')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                >
                  <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
                  {t('dashboards.refresh')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleExportCSV}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('dashboards.exportCSV')}
                </DropdownMenuItem>
                {onUnassignSlot && (
                  <DropdownMenuItem onClick={onUnassignSlot}>
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('dashboards.layouts.unassignSlot', 'Remover do slot')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={handleTogglePin}
                >
                  {dashboard.is_active ? (
                    <>
                      <PinOff className="h-4 w-4 mr-2" />
                      {t('dashboards.unpinDashboard')}
                    </>
                  ) : (
                    <>
                      <Pin className="h-4 w-4 mr-2" />
                      {t('dashboards.pinDashboard')}
                    </>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Metric (tile) cabe em qualquer altura — não força min-h, senão
            o flex-1 cria espaço extra abaixo do conteúdo e o card mostra o
            número desalinhado para baixo em tiles pequenos. */}
        <div className={cn('flex-1', dashboard.type !== 'metric' && 'min-h-[200px]')}>
          <DashboardContent dashboard={dashboard} onRefreshReady={handleRefreshReady} />
        </div>
      </Card>

      <DashboardSettings
        dashboard={dashboard}
        open={showSettings}
        onOpenChange={setShowSettings}
        onSave={(request) => {
          onUpdate({ dashboardId: dashboard.id, request });
        }}
      />
    </div>
  );
};
